import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DialogFrame, DialogLink } from "component/shared/dialog-frame";
import { OutlinedButton } from "component/shared/outlined-button";
import { PlatformChoice } from "type/platform-choice";
import { CONTACT_SUPPORT_URL } from "../constants";
import { PlatformRadioGroup } from "../platform-radio-group";
import { styles } from "./style";

interface OwnProps {
	open: boolean;
	value: PlatformChoice;
	/** While the save runs the modal stays open, with the buttons disabled. */
	saving?: boolean;
	/** A failed save, shown inline so the user can retry without losing their choice. */
	error?: string;
	onClose: () => void;
	/** Called with the chosen platform; the page decides what a change sets off. */
	onSave: (choice: PlatformChoice) => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * Edit Platform (VPLAT 7): WordPress / Wix / Other behind a warning, since switching can stop IDX
 * content displaying on the client's site.
 */
const Component: React.FC<Props> = ({ classes = {}, open, value, saving = false, error, onClose, onSave }) => {
	const [choice, setChoice] = useState<PlatformChoice>(value);
	// Each opening starts from the client's current platform, not the last unsaved pick.
	useEffect(() => {
		if (open) {
			setChoice(value);
		}
	}, [open, value]);
	return (
		<DialogFrame
			open={open}
			onClose={() => !saving && onClose()}
			title="Edit Platform"
			titleId="edit-platform-title"
			closeDisabled={saving}
			actions={
				<>
					<OutlinedButton disabled={saving} onClick={onClose}>
						Cancel
					</OutlinedButton>
					<Mui.Button variant="contained" color="primary" disabled={saving} onClick={() => onSave(choice)}>
						{saving ? "Saving..." : "Save Changes"}
					</Mui.Button>
				</>
			}
		>
			<Mui.Alert severity="warning" icon={<Icons.AlertTriangle size={20} />} className={classes.alert}>
				<Mui.Typography className={classes.warning}>
					Changing these settings may prevent IDX content from displaying on your website. We
					recommend{" "}
					<DialogLink href={CONTACT_SUPPORT_URL} target="_blank" rel="noopener noreferrer" className={classes.link}>
						contacting support
					</DialogLink>{" "}
					if you need to change platforms.
				</Mui.Typography>
			</Mui.Alert>
			<div>
				<Mui.Typography id="edit-platform-field" className={classes.fieldLabel}>
					Platform
				</Mui.Typography>
				<PlatformRadioGroup value={choice} onChange={setChoice} aria-labelledby="edit-platform-field" />
			</div>
			{error && (
				<Mui.Alert severity="error" role="alert">
					{error}
				</Mui.Alert>
			)}
		</DialogFrame>
	);
};

export const EditPlatformDialog = withStyles(Component, styles);
